import { useEffect, useRef, useState } from "react";

type TopBannerProps = {
  onExportRankings: () => void;
  onExportCheatsheetPdf: () => void;
  onImportRankings: () => void;
  onOpenDraftSync: () => void;
  uiScale?: number;
};

export default function TopBanner({
  onExportRankings,
  onExportCheatsheetPdf,
  onImportRankings,
  onOpenDraftSync,
  uiScale = 1,
}: TopBannerProps) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isCompactViewport, setIsCompactViewport] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= 900;
  });
  const settingsMenuRef = useRef<HTMLDivElement>(null);

  // Settings: click outside + ESC
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const updateViewportMode = () => setIsCompactViewport(window.innerWidth <= 900);
    updateViewportMode();
    window.addEventListener("resize", updateViewportMode);
    window.addEventListener("orientationchange", updateViewportMode);
    return () => {
      window.removeEventListener("resize", updateViewportMode);
      window.removeEventListener("orientationchange", updateViewportMode);
    };
  }, []);

  useEffect(() => {
    if (!settingsOpen) return;

    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (settingsMenuRef.current && !settingsMenuRef.current.contains(target)) setSettingsOpen(false);
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSettingsOpen(false);
    };

    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [settingsOpen]);

  const actionBtnBase: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: (isCompactViewport ? 6 : 8) * uiScale,
    minHeight: (isCompactViewport ? 28 : 32) * uiScale,
    padding: `${(isCompactViewport ? 6 : 8) * uiScale}px 0`,
    borderRadius: 0,
    border: "none",
    background: "transparent",
    color: "rgba(245,247,255,0.76)",
    fontWeight: 700,
    fontSize: (isCompactViewport ? 13 : 15) * uiScale,
    lineHeight: 1,
    whiteSpace: "nowrap",
    cursor: "pointer",
    transition: "color 180ms ease, opacity 180ms ease",
  };

  return (
    <div style={{ position: "relative", zIndex: 20 }}>
      <div
        style={{
          width: "100%",
          display: isCompactViewport ? "flex" : "grid",
          flexDirection: isCompactViewport ? "column" : undefined,
          gridTemplateColumns: isCompactViewport ? undefined : "minmax(220px, auto) minmax(0, 1fr) minmax(320px, auto)",
          alignItems: "center",
          gap: (isCompactViewport ? 10 : 16) * uiScale,
          padding: `${(isCompactViewport ? 10 : 12) * uiScale}px ${(isCompactViewport ? 12 : 20) * uiScale}px`,
          borderRadius: 0,
          border: "none",
          overflow: "visible",
          boxShadow: "none",
          background:
            "linear-gradient(135deg, rgba(7,10,18,0.96) 0%, rgba(10,18,34,0.94) 52%, rgba(16,39,68,0.92) 100%)",
          backdropFilter: "none",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            minWidth: 0,
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <a
            href="https://fantasydownload.com"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="FantasyDownload"
            style={{ display: "inline-flex", alignItems: "center", cursor: "pointer", maxWidth: "100%" }}
          >
            <img
              src="/fantasydownloadlogo.png"
              alt="FantasyDownload"
              style={{
                height: (isCompactViewport ? 46 : 60) * uiScale,
                width: "auto",
                maxWidth: "100%",
                objectFit: "contain",
                filter: "drop-shadow(0 8px 18px rgba(0,0,0,0.42))",
              }}
              draggable={false}
            />
          </a>
        </div>
        {!isCompactViewport ? <div /> : null}

        <div
          style={{
            display: "flex",
            justifyContent: isCompactViewport ? "flex-start" : "flex-end",
            alignItems: "center",
            gap: (isCompactViewport ? 16 : 22) * uiScale,
            minWidth: 0,
            width: isCompactViewport ? "100%" : undefined,
            overflowX: isCompactViewport ? "auto" : "visible",
            overflowY: "visible",
            WebkitOverflowScrolling: "touch",
            paddingBottom: isCompactViewport ? 2 : 0,
          }}
        >
          <a
            href="/draftcompanion"
            style={{
              ...actionBtnBase,
              textDecoration: "none",
            }}
          >
            <span>Draft Companion</span>
          </a>

          <button type="button" onClick={onOpenDraftSync} style={actionBtnBase}>
            <span>Sync Sleeper</span>
          </button>

          <div ref={settingsMenuRef} style={{ position: "relative" }}>
            <button
              onClick={() => setSettingsOpen((v) => !v)}
              title="Settings"
              aria-label="Settings"
              style={{
                ...actionBtnBase,
                width: 24 * uiScale,
                minWidth: 24 * uiScale,
                minHeight: 24 * uiScale,
                padding: 0,
                color: settingsOpen ? "#ffffff" : "rgba(245,247,255,0.76)",
                opacity: settingsOpen ? 1 : 0.9,
              }}
            >
              <svg width={18 * uiScale} height={18 * uiScale} viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M4 6h16" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" />
                <path d="M4 12h16" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" />
                <path d="M4 18h16" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" />
              </svg>
            </button>

            {settingsOpen && (
              <div
                style={{
                  position: "absolute",
                  right: 0,
                  top: 52 * uiScale,
                  minWidth: 220 * uiScale,
                  padding: 8 * uiScale,
                  borderRadius: 18 * uiScale,
                  border: "1px solid rgba(255,255,255,0.08)",
                  background: "rgba(10,15,26,0.98)",
                  boxShadow: "0 22px 44px rgba(0,0,0,0.35)",
                  zIndex: 5000,
                  backdropFilter: "blur(16px)",
                }}
              >
                <button
                  onClick={() => {
                    setSettingsOpen(false);
                    onExportRankings();
                  }}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: `${12 * uiScale}px ${14 * uiScale}px`,
                    borderRadius: 14 * uiScale,
                    border: "none",
                    background: "transparent",
                    color: "rgba(248,250,255,0.92)",
                    fontWeight: 800,
                    fontSize: 14 * uiScale,
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.06)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  Export Rankings
                </button>

                <button
                  onClick={() => {
                    setSettingsOpen(false);
                    onExportCheatsheetPdf();
                  }}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: `${12 * uiScale}px ${14 * uiScale}px`,
                    borderRadius: 14 * uiScale,
                    border: "none",
                    background: "transparent",
                    color: "rgba(248,250,255,0.92)",
                    cursor: "pointer",
                    fontWeight: 700,
                    fontSize: 14 * uiScale,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.06)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  Export Cheatsheet (PDF)
                </button>

                <button
                  onClick={() => {
                    setSettingsOpen(false);
                    onImportRankings();
                  }}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: `${12 * uiScale}px ${14 * uiScale}px`,
                    borderRadius: 14 * uiScale,
                    border: "none",
                    background: "transparent",
                    color: "rgba(248,250,255,0.92)",
                    fontWeight: 800,
                    fontSize: 14 * uiScale,
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.06)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  Import Rankings
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
